import React, {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import styles from './GridFilter.module.scss';
import LocalStorage from '../../../../Services/LocalStorage';
import { useAppContext } from '../../../../Services/AppContext';
import { useEffectAsync } from '../../../../helpers';
import {
	useLazyGetGridFilterQuery,
	useSaveGridFilterMutation,
} from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import { isEqual, cloneDeep } from 'lodash';
import { joinClasses } from '../../../../Utils/className.util';
import Text from '../../../../Components/Base/Text/Text';
import {
	getGridFilterItemFormKey,
	GridFilterItemProps,
} from './GridFilterItem';

export interface GridFilterProps<
	TValues extends Record<string, any> = Record<string, any>,
> {
	children: React.ReactElement | React.ReactElement[];
	onChange: (values: TValues) => void;
	onReady?: () => void;
	storageKey?: string;
	storageType?: 'api' | 'local';
	isFilterOpen?: boolean;
	dataCount?: number | null;
	confirmButton?: boolean;
	defaultValues?: object;
	customButtons?: React.ReactElement;
	formClassName?: string;
}

const GridFilter = <TValues extends Record<string, any> = Record<string, any>>({
	children,
	onChange,
	onReady,
	storageKey,
	storageType = 'api',
	isFilterOpen = false,
	dataCount,
	confirmButton = false,
	defaultValues,
	customButtons,
	formClassName,
}: GridFilterProps<TValues>) => {
	const [isOpen, setIsOpen] = useState(false);
	const [hasMoreFilters, setHasMoreFilters] = useState(true);
	const [items, setItems] = useState<React.ReactNode[]>([]);
	const ref = useRef<any>();
	const formMethods = useForm({
		defaultValues: storageKey ? {} : defaultValues || {},
	});
	const { workplaceId } = useAppContext();
	const [getStoredFilter] = useLazyGetGridFilterQuery();
	const gridFilterKey = `${storageKey}_${workplaceId}`;
	const [saveGridFilter] = useSaveGridFilterMutation();
	const { t } = useTranslation();
	const [lastDefValues, setLastDefValues] = useState<object>();
	const filterRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>(
		{},
	);
	const isFirstRender = useRef(true);
	const filterWidths = useRef<{ [key: string]: number }>({});
	const controlsRef = useRef<HTMLDivElement>(null);
	const [isStorageLoaded, setIsStorageLoaded] = useState(false);

	const values = formMethods.watch();
	const memoizedValues = useMemo(() => JSON.stringify(values), [values]);

	const hasFilter = useMemo(() => {
		const filters = Object.values(values).filter(val => !!val);
		return filters.length > 0;
	}, [values]);

	const loadDataFromStorage = (key: string) => {
		const data = LocalStorage.get(key);
		return data || {};
	};

	const saveToLocalStorage = (key: string, data: any) => {
		if (data) {
			LocalStorage.set(key, data);
		}
	};

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
		}
	}, []);

	useEffect(() => {
		if (storageKey && !isStorageLoaded) {
			return;
		}

		if (!isEqual(defaultValues, lastDefValues)) {
			setLastDefValues(cloneDeep(defaultValues));
			formMethods.reset(defaultValues);
		}
	}, [defaultValues, isStorageLoaded]);

	useLayoutEffect(() => {
		handleCalculateFilterDimensions();
	}, [isOpen, children, hasFilter, hasMoreFilters, memoizedValues]);

	useEffect(() => {
		window.addEventListener('resize', handleCalculateFilterDimensions);

		return () => {
			window.removeEventListener('resize', handleCalculateFilterDimensions);
		};
	}, []);

	useEffectAsync(async () => {
		try {
			if (!storageKey) {
				return;
			}

			const data =
				storageType === 'api'
					? (await getStoredFilter({ key: gridFilterKey }).unwrap()).userFilter
							?.value
					: loadDataFromStorage(gridFilterKey);

			const hasStoredData = !!data && Object.keys(data).length > 0;

			if (hasStoredData) {
				formMethods.reset(data);
				await formMethods.handleSubmit(() => onChange(data))();
			} else if (defaultValues) {
				formMethods.reset(defaultValues);
			}

			setLastDefValues(cloneDeep(defaultValues));
		} finally {
			setIsStorageLoaded(true);
			onReady?.();
		}
	}, []);

	useEffect(() => {
		setIsOpen(isFilterOpen);
	}, [isFilterOpen]);

	// .controls block width when it has to be rendered but is not in the DOM
	// yet: min-width 180px from SCSS + 24px wrapper gap
	const CONTROLS_RESERVED_WIDTH = 204;

	const getAvailableWidths = () => {
		const wrapperWidth = ref.current?.clientWidth || 0;
		const controlsWidth = controlsRef.current
			? controlsRef.current.offsetWidth + 24
			: 0;
		const rowWidth = wrapperWidth + controlsWidth;
		const hasActiveFilter = Object.values(formMethods.getValues()).some(
			value => !!value,
		);
		// reset/confirm buttons keep the controls block rendered even
		// without the toggle, so its space cannot be reclaimed
		const controlsAlwaysVisible = confirmButton || hasActiveFilter;
		const withControls =
			rowWidth - Math.max(controlsWidth, CONTROLS_RESERVED_WIDTH);

		return {
			withoutControls: controlsAlwaysVisible ? withControls : rowWidth,
			withControls,
		};
	};

	const getChildKey = (child: React.ReactNode, index: number): string => {
		if (React.isValidElement(child) && child.key !== null) {
			return String(child.key);
		}

		return String(index);
	};

	const isChildActive = (child: React.ReactNode): boolean => {
		if (!React.isValidElement(child)) {
			return false;
		}

		const formKey = getGridFilterItemFormKey(
			child.props as Partial<GridFilterItemProps>,
		);

		if (!formKey) {
			return false;
		}

		const value = (formMethods.getValues() as Record<string, unknown>)[formKey];

		return Array.isArray(value) ? value.length > 0 : !!value;
	};

	const handleCalculateFilterDimensions = () => {
		if (!ref.current) {
			return;
		}

		const visibleChildren = React.Children.toArray(children).filter(child => {
			if (!React.isValidElement(child)) {
				return true;
			}

			return child.props?.visible !== false;
		});

		if (isFirstRender.current || isOpen) {
			setItems(visibleChildren as React.ReactNode[]);
			isFirstRender.current = false;

			requestAnimationFrame(() => {
				visibleChildren.forEach((child, index) => {
					const childKey = getChildKey(child, index);
					const width = filterRefs.current[childKey]?.current?.clientWidth;

					if (width) {
						filterWidths.current[childKey] = width;
					}
				});

				const fullWidth = visibleChildren.reduce((acc, child, index) => {
					return (
						acc + (filterWidths.current[getChildKey(child, index)] || 0) + 6
					);
				}, 0);

				setHasMoreFilters(fullWidth > getAvailableWidths().withoutControls);
			});

			return;
		}

		let fullWidth = 0;

		visibleChildren.forEach((child, index) => {
			const childKey = getChildKey(child, index);
			const measuredWidth = filterRefs.current[childKey]?.current?.clientWidth;

			if (measuredWidth) {
				filterWidths.current[childKey] = measuredWidth;
			}

			fullWidth += (filterWidths.current[childKey] || 0) + 6;
		});

		const { withoutControls, withControls } = getAvailableWidths();

		if (fullWidth <= withoutControls) {
			setHasMoreFilters(false);
			setItems(visibleChildren as React.ReactNode[]);
			return;
		}

		const included = visibleChildren.map(child => isChildActive(child));

		let currentWidth = included.reduce((acc, isIncluded, index) => {
			return isIncluded
				? acc +
						(filterWidths.current[getChildKey(visibleChildren[index], index)] ||
							0) +
						6
				: acc;
		}, 0);

		for (let index = 0; index < visibleChildren.length; index++) {
			if (included[index]) {
				continue;
			}

			const elementWidth =
				filterWidths.current[getChildKey(visibleChildren[index], index)];

			if (!elementWidth) {
				break;
			}

			const fits = currentWidth + elementWidth + 6 < withControls;

			if (!fits) {
				break;
			}

			currentWidth += elementWidth + 6;
			included[index] = true;
		}

		const tempItems: React.ReactNode[] = visibleChildren.filter(
			(_, index) => included[index],
		);

		// the first filter stays visible even when it does not fit,
		// otherwise narrow screens would show no filter at all
		if (tempItems.length === 0 && visibleChildren.length > 0) {
			tempItems.push(visibleChildren[0] as React.ReactNode);
		}

		setHasMoreFilters(tempItems.length < visibleChildren.length);
		setItems(tempItems);
	};

	const handleToggleMore = () => {
		setIsOpen(prev => !prev);
	};

	const handleOnChange = ({ enableAutocomplete }: any) => {
		if (enableAutocomplete) {
			formMethods.handleSubmit(onChange)();
		}
	};

	const handleResetFilter = () => {
		const currentValues = formMethods.getValues();

		const resetValues = Object.keys(currentValues).reduce((acc, key) => {
			// @ts-ignore
			acc[key] = null;
			return acc;
		}, {});

		formMethods.reset(resetValues);
		formMethods.handleSubmit(() => onChange({} as TValues))();
	};

	useEffectAsync(async () => {
		if (!isStorageLoaded) {
			return;
		}

		if (storageKey && values) {
			if (storageType === 'api') {
				await saveGridFilter({
					input: {
						key: gridFilterKey,
						data: values,
					},
				});
			} else {
				saveToLocalStorage(gridFilterKey, values);
			}
		}
	}, [memoizedValues]);

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					hasFilter ? styles.active : '',
				])}>
				{hasFilter && (
					<Text
						className={styles.activeBadge}
						tx='component.gridFilter.activeBadge'
					/>
				)}

				<FormWrapper
					style={{ flex: 1 }}
					formMethods={formMethods}
					className={formClassName}
					onSubmit={() => handleOnChange({ enableAutocomplete: true })}>
					<div className={styles.form} ref={ref}>
						{items.map((item: any, index: number) => {
							const childKey = getChildKey(item, index);

							if (!filterRefs.current[childKey]) {
								filterRefs.current[childKey] =
									React.createRef<HTMLDivElement>();
							}

							const elementRef = filterRefs.current[childKey];

							return (
								<React.Fragment key={childKey}>
									{React.cloneElement(item, {
										...item.props,
										ref: elementRef,
										customOnChange: handleOnChange,
									})}
								</React.Fragment>
							);
						})}
					</div>
				</FormWrapper>

				{(confirmButton || hasMoreFilters || hasFilter) && (
					<div className={styles.controls} ref={controlsRef}>
						{confirmButton && (
							<LinkButton
								title='base.shared.confirm'
								theme={Theme.BLUE}
								onClick={() => handleOnChange({ enableAutocomplete: true })}
							/>
						)}

						{hasMoreFilters && (
							<div className={styles.toggleButton}>
								<LinkButton
									title='base.additionalFilters'
									theme={Theme.BLUE}
									onClick={handleToggleMore}
								/>
								{isOpen ? (
									<img
										src='/dist/icons/chevron-down-backgrounded-open.svg'
										alt='arrow up'
									/>
								) : (
									<img
										src='/dist/icons/chevron-down-backgrounded.svg'
										alt='arrow down'
									/>
								)}
							</div>
						)}

						{hasFilter && (
							<LinkButton
								title='ublaboo_datagrid.reset_filter'
								theme={Theme.BLUE}
								onClick={handleResetFilter}
							/>
						)}
					</div>
				)}

				{customButtons}
			</div>

			{!!dataCount && (
				<div className={styles.filterInfo}>
					<div className={styles.count}>
						{t('base.numberOfRecords')}: {dataCount}
					</div>
				</div>
			)}
		</>
	);
};

export default GridFilter;
